import React, { useEffect, useMemo } from "react";

type RemoteFile = {
  id?: number | string;
  url?: string;
  key: string;
  name: string;
  mimetype?: string;
  size?: number;
};

type FileItem = RemoteFile;

type Props = {
  files: FileItem[];
  onRemove?: (index: number) => void;
  onDownload?: (prams: RemoteFile) => void;
  maxHeight?: string;
};

function formatBytes(bytes = 0) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export const FileViewer: React.FC<Props> = ({
  files,
  onRemove,
  onDownload,
  maxHeight = "16rem",
}) => {
  // Gerar e limpar URLs (boa prática)
  const fileUrls = useMemo(() => {
    return files.map((f) =>
      f instanceof File ? URL.createObjectURL(f) : f.url
    );
  }, [files]);

  useEffect(() => {
    return () => {
      fileUrls.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [fileUrls]);

  const containerStyle: React.CSSProperties = {
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: 8,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    background: "#ffffff",
    maxHeight,
    overflow: "auto",
  };

  const itemStyle: React.CSSProperties = {
    display: "flex",
    gap: 12,
    alignItems: "center",
    padding: "8px 6px",
    borderRadius: 6,
    boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.03)",
  };

  const thumbStyle: React.CSSProperties = {
    minWidth: 56,
    minHeight: 56,
    maxWidth: 56,
    maxHeight: 56,
    borderRadius: 6,
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    color: "#475569",
  };

  const metaStyle: React.CSSProperties = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 0,
  };

  const nameStyle: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 600,
    color: "#0f172a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const secondaryStyle: React.CSSProperties = {
    fontSize: 12,
    color: "#64748b",
  };

  const buttonStyle: React.CSSProperties = {
    textDecoration: "none",
    fontSize: 13,
    padding: "6px 8px",
    borderRadius: 6,
    border: "1px solid rgba(0,0,0,0.06)",
    background: "#fff",
    cursor: "pointer",
  };

  return (
    <div style={containerStyle}>
      {files.length === 0 && (
        <div style={{ color: "#64748b", fontSize: 13 }}>
          Nenhum arquivo anexado.
        </div>
      )}

      {files.map((file, idx) => {
        const name = file.name;
        const size = file.size ?? 0;
        const mimetype = file.mimetype ?? "Desconhecido";

        return (
          <div key={`${file.key}-${idx}`} style={itemStyle}>
            <div style={thumbStyle}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
              </svg>
            </div>

            <div style={metaStyle}>
              <div style={nameStyle} title={name}>
                {name}
              </div>
              <div style={secondaryStyle}>
                {mimetype} • {formatBytes(size)}
              </div>
            </div>

            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {onDownload && (
                <span
                  style={buttonStyle}
                  title="Baixar"
                  onClick={() => onDownload(file)}
                >
                  Baixar
                </span>
              )}

              {onRemove && (
                <button
                  onClick={() => onRemove(idx)}
                  style={buttonStyle}
                  title="Remover"
                >
                  Remover
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
